import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { addDoc, serverTimestamp, updateDoc } from "firebase/firestore";
import { useAuth } from "../auth";
import { useI18n } from "../lib/i18n";
import { VIDEO_ACCEPT } from "../lib/media";
import { inspectVideoFiles, sortVideoFiles } from "../lib/parts";
import { mb, uploadMaterial, type UploadProgress } from "../lib/upload";

/**
 * ① 動画とレシピメモをアップロードする画面。
 *
 * 動画は 2GB を超えうる(実測 2.1GB)ので resumable upload で直接 GCS へ送る。
 * Cloud Functions を経由させない(関数のペイロード上限に当たるため)。
 *
 * 720p への変換はここでは行わない。ffmpeg.wasm は 2GB 級の入力でメモリ上限に
 * 当たり実用にならないため、Cloud Run Jobs 側の前処理で行う
 * (docs/dashboard-design.md §6)。
 */
export function NewDish() {
  const { user, paths } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();

  const [name, setName] = useState("");
  const [nameJa, setNameJa] = useState("");
  const [hints, setHints] = useState("");
  // DJI Osmo Action 5 Pro は約4GBごとに自動分割する。1回の調理が複数ファイルになる
  const [videos, setVideos] = useState<File[]>([]);
  const [docs, setDocs] = useState<File[]>([]);
  const [progress, setProgress] = useState<Record<string, UploadProgress>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function move(i: number, delta: number) {
    const j = i + delta;
    if (j < 0 || j >= videos.length) return;
    const next = [...videos];
    [next[i], next[j]] = [next[j], next[i]];
    setVideos(next);
  }

  // 保存先の組み立てと contentType の補完(#55)は lib/upload.ts が正。
  // 差し替え画面(DishMaterials)と同じ経路を通す
  async function put(dishId: string, file: File, kind: "video" | "doc"): Promise<string> {
    return uploadMaterial(dishId, file, kind, (path, st) =>
      setProgress((p) => ({ ...p, [path]: st })),
    );
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!user || videos.length === 0) return;
    if (!paths) {
      setError(t("テナントが未確定です。ログインし直してください"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      // 先に品目を作って ID を得る。アップロード先パスに ID を含めるため。
      const dishRef = await addDoc(paths.dishes(), {
        name: name.trim(),
        nameJa: nameJa.trim() || null,
        hints: hints.trim() || null,
        status: "draft",
        version: 0,
        isGolden: false,
        createdBy: user.uid,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        currentAnalysisPath: null,
        // 作成者は自動的に担当に入れる(決定1。入れないと自分で作った品目が見えない)
        assigneeUids: [user.uid],
        sourceVideoPath: null,
        recipeDocPaths: [],
      });

      // 選んだ順（＝画面で確認した並び）が結合順の正
      const videoPaths: string[] = [];
      for (const v of videos) videoPaths.push(await put(dishRef.id, v, "video"));
      const docPaths: string[] = [];
      for (const f of docs) docPaths.push(await put(dishRef.id, f, "doc"));

      await updateDoc(paths.dishDoc(dishRef.id), {
        sourceVideoPaths: videoPaths,
        // 単一パスは後方互換のため残す（既存の読み手が居る）
        sourceVideoPath: videoPaths[0],
        recipeDocPaths: docPaths,
        updatedAt: serverTimestamp(),
      });

      navigate(`/dishes/${dishRef.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const videoWarnings = inspectVideoFiles(videos);
  const items = Object.entries(progress);

  return (
    <div className="page">
      <h1>{t("新しい品目")}</h1>
      <form onSubmit={onSubmit} className="card">
        <label htmlFor="name">{t("料理名 *")}</label>
        <input
          id="name"
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="香煎豬肝(豚レバーの香り炒め)"
        />

        <label htmlFor="nameJa">{t("料理名の読み")}</label>
        <input
          id="nameJa"
          value={nameJa}
          onChange={(e) => setNameJa(e.target.value)}
          placeholder="シャンジェンジューガン"
        />

        <label htmlFor="hints">{t("補足メモ(仕込み済みの前提など)")}</label>
        <textarea
          id="hints"
          rows={4}
          value={hints}
          onChange={(e) => setHints(e.target.value)}
          placeholder={t("例: レバーはカット・水洗い済み。大根漬けと自家製レバーソースは別途仕込み済み。")}
        />
        <p className="muted small">
          {t("料理名の意味と、調理開始時点で仕込み済みの食材をここに書いてください。映像に写っていない前提は、書かないと欠落したまま生成されます。")}
        </p>

        <label htmlFor="video">{t("調理動画 *")}</label>
        <input
          id="video"
          type="file"
          accept={VIDEO_ACCEPT}
          multiple
          required
          onChange={(e) => setVideos(sortVideoFiles([...(e.target.files ?? [])]))}
        />
        <p className="muted small">
          {t("カメラが自動で分割したファイルはまとめて選んでください。1本の録画として結合して解析します（DJI Osmo Action 5 Pro は約10〜13分ごとに分割します）。")}
        </p>
        {videos.length > 0 && (
          <ol className="parts">
            {videos.map((v, i) => (
              <li key={v.name + i}>
                <span className="no">
                  {videos.length > 1 ? t("{n}本目", { n: i + 1 }) : t("1本のみ")}
                </span>
                <span className="nm">{v.name}</span>
                <span className="sz">{mb(v.size)}</span>
                {videos.length > 1 && (
                  <span className="mvs">
                    <button type="button" disabled={i === 0 || busy} onClick={() => move(i, -1)}>↑</button>
                    <button type="button" disabled={i === videos.length - 1 || busy} onClick={() => move(i, 1)}>↓</button>
                  </span>
                )}
              </li>
            ))}
            <li className="total">
              {videos.length > 1
                ? t("{n} パートを結合して1本として解析します（合計 {size}）", {
                    n: videos.length,
                    size: mb(videos.reduce((n, v) => n + v.size, 0)),
                  })
                : t("合計 {size}", { size: mb(videos[0].size) })}
            </li>
          </ol>
        )}
        {videoWarnings.length > 0 && (
          <div className="warn">
            {videoWarnings.map((w) => (
              <div key={w}>⚠ {t(w)}</div>
            ))}
          </div>
        )}

        <label htmlFor="docs">{t("レシピ・メモ(PDF / 画像・複数可)")}</label>
        <input
          id="docs"
          type="file"
          accept="application/pdf,image/*"
          multiple
          onChange={(e) => setDocs(Array.from(e.target.files ?? []))}
        />

        <button type="submit" disabled={busy || !name.trim() || videos.length === 0}>
          {busy ? t("アップロード中…") : t("アップロードして登録")}
        </button>
        {error && <p className="error">{error}</p>}
      </form>

      {items.length > 0 && (
        <div className="card">
          <h2>{t("アップロード状況")}</h2>
          {items.map(([key, p]) => (
            <div key={key} className="progress-row">
              <div className="progress-label">
                <span>{p.name}</span>
                <span className="muted">
                  {p.done ? t("完了") : `${mb(p.transferred)} / ${mb(p.total)}`}
                </span>
              </div>
              <progress value={p.transferred} max={p.total || 1} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
